/// <reference path="../types/connection-manager.d.ts" />
import { GlobalApisService } from "@martech-unit-projects/connection-manager";
import type { IResponse } from "./types";

/**
 * Singleton wrapper around the @martech-unit-projects/connection-manager npm package
 * Provides a centralized instance for all API calls
 */
export class ConnectionManagerClient {
  private static instance: ConnectionManagerClient;
  private globalService: GlobalApisService;
  private initialized: boolean = false;
  private initPromise: Promise<boolean> | null = null;

  private constructor() {
    this.globalService = new GlobalApisService();
  }

  /**
   * Get singleton instance of ConnectionManagerClient
   */
  static getInstance(): ConnectionManagerClient {
    if (!ConnectionManagerClient.instance) {
      ConnectionManagerClient.instance = new ConnectionManagerClient();
    }
    return ConnectionManagerClient.instance;
  }

  /**
   * Initialize the connection manager library
   * @param source - Source identifier (e.g., 'WEB')
   * @param env - Environment (e.g., 'PRODUCTION', 'UAT', 'iUAT')
   * @returns Promise that resolves when initialization is complete
   */
  async initLib(source: string, env: string): Promise<boolean> {
    // Return existing initialization promise if already initializing
    if (this.initPromise) {
      return this.initPromise;
    }

    // Return true if already initialized with same config
    if (this.initialized) {
      return Promise.resolve(true);
    }

    // Create new initialization promise
    this.initPromise = new Promise((resolve, reject) => {
      this.globalService.initLib(source, env).subscribe({
        next: () => {
          this.initialized = true;
          this.initPromise = null;
          resolve(true);
        },
        error: (err: IResponse<unknown>) => {
          this.initPromise = null;
          reject(err);
        },
      });
    });

    return this.initPromise;
  }

  /**
   * Call API through connection manager gateway
   * @param operationId - Operation identifier
   * @param requestData - Request payload
   * @param connectionFrom - Connection source
   * @param additionalHeaders - Additional headers
   * @param source - Source identifier
   * @returns Promise with API response
   */
  async callApi<TResponse, TRequest = unknown>(
    operationId: string,
    requestData: TRequest,
    connectionFrom: string,
    additionalHeaders: Record<string, string | number>,
    source: string
  ): Promise<IResponse<TResponse>> {
    return new Promise((resolve, reject) => {
      this.globalService
        .gateWayApiCallingV3<TResponse, TRequest>(
          operationId,
          requestData,
          connectionFrom,
          additionalHeaders,
          source
        )
        .subscribe({
          next: (res: IResponse<unknown>) => {
            resolve(res as IResponse<TResponse>);
          },
          error: (err: any) => {
            // Handle promise-based errors
            if (err instanceof Promise) {
              err
                .then((actualError) => reject(actualError))
                .catch((catchError) => reject(catchError));
            } else {
              reject(err);
            }
          },
        });
    });
  }

  /**
   * Reset the client instance (useful for testing)
   */
  static reset(): void {
    if (ConnectionManagerClient.instance) {
      ConnectionManagerClient.instance.initialized = false;
      ConnectionManagerClient.instance.initPromise = null;
    }
    ConnectionManagerClient.instance = null!;
  }
}
